import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Table from "../../../core/common/dataTable/index";
import { activeList } from "../../../core/common/selectoption/selectoption";
import CommonSelect from "../../../core/common/commonSelect";
import TooltipOption from "../../../core/common/tooltipOption";
import { all_routes } from "../../router/all_routes";
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice";
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice";
import {
  getAllSections,
  createSection,
  updateSection,
  deleteSection,
} from "../../../service/classes";

type SectionRow = {
  _id: string;
  name: string;
  status: "active" | "inactive";
};

// This replaces a page that used to render a hardcoded demo JSON file
// (core/data/json/class-section) regardless of which campus/session was
// selected — every campus showed the exact same fake rows. Sections are
// real, per-campus documents (see models/Section.js); this page now lists,
// creates, renames and deletes the current campus's own copies.
const ClassSection = () => {
  const routes = all_routes;
  const currentBranch = useSelector(selectCurrentBranch);
  const currentSession = useSelector(selectCurrentSession);

  const [sections, setSections] = useState<SectionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState(activeList[0]);

  const [newSectionName, setNewSectionName] = useState("");
  const [newSectionActive, setNewSectionActive] = useState(true);
  const [creating, setCreating] = useState(false);

  const [editingSection, setEditingSection] = useState<SectionRow | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [deletingSection, setDeletingSection] = useState<SectionRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

  const fetchSections = async () => {
    if (!currentBranch?._id || !currentSession?._id) return;
    setLoading(true);
    try {
      const data = await getAllSections({
        branch: currentBranch._id,
        session: currentSession._id,
        limit: 200,
      });
      setSections(data.sections || []);
    } catch (err) {
      console.error("Error fetching sections:", err);
      toast.error("Failed to load sections");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBranch?._id, currentSession?._id]);

  const closeModal = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.setAttribute("data-bs-dismiss", "modal");
      el.click();
      el.removeAttribute("data-bs-dismiss");
    }
  };

  const handleAddSection = async () => {
    const trimmed = newSectionName.trim();
    if (!trimmed) {
      toast.error("Section name is required");
      return;
    }
    if (!currentSession?._id) {
      toast.error("Select an academic year first");
      return;
    }
    setCreating(true);
    try {
      await createSection({
        name: trimmed,
        session: currentSession._id,
        status: newSectionActive ? "active" : "inactive",
      });
      toast.success(`Section "${trimmed}" added for all campuses`);
      setNewSectionName("");
      setNewSectionActive(true);
      closeModal("add_class_section");
      await fetchSections();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to add section");
    } finally {
      setCreating(false);
    }
  };

  const handleEditClick = (row: SectionRow) => {
    setEditingSection({ ...row });
  };

  const handleSaveEdit = async () => {
    if (!editingSection) return;
    const trimmed = editingSection.name.trim();
    if (!trimmed) {
      toast.error("Section name is required");
      return;
    }
    setSavingEdit(true);
    try {
      await updateSection(editingSection._id, {
        name: trimmed,
        status: editingSection.status,
      });
      toast.success("Section updated");
      closeModal("edit_class_section");
      setEditingSection(null);
      await fetchSections();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to update section");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteClick = (row: SectionRow) => {
    setDeletingSection(row);
  };

  const handleConfirmDelete = async () => {
    if (!deletingSection) return;
    setDeleting(true);
    try {
      await deleteSection(deletingSection._id);
      toast.success(`Section "${deletingSection.name}" deleted`);
      closeModal("delete-modal");
      setDeletingSection(null);
      await fetchSections();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to delete section");
    } finally {
      setDeleting(false);
    }
  };

  const filteredSections = useMemo(() => {
    let rows = sections;
    if (statusFilter?.value && statusFilter.value !== "Select") {
      rows = rows.filter((s) => s.status === statusFilter.value);
    }
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      rows = rows.filter((s) => s.name.toLowerCase().includes(q));
    }
    return rows;
  }, [sections, statusFilter, searchText]);

  const columns = [
    {
      title: "Section Name",
      dataIndex: "name",
      sorter: (a: SectionRow, b: SectionRow) => a.name.localeCompare(b.name),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string) => (
        <span
          className={`badge d-inline-flex align-items-center ${
            status === "active" ? "badge-soft-success" : "badge-soft-danger"
          }`}
        >
          <i className="ti ti-circle-filled fs-5 me-1" />
          {status === "active" ? "Active" : "Inactive"}
        </span>
      ),
      sorter: (a: SectionRow, b: SectionRow) => a.status.localeCompare(b.status),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: SectionRow) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#edit_class_section"
                  onClick={() => handleEditClick(record)}
                >
                  <i className="ti ti-edit-circle me-2" />
                  Edit
                </Link>
              </li>
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#delete-modal"
                  onClick={() => handleDeleteClick(record)}
                >
                  <i className="ti ti-trash-x me-2" />
                  Delete
                </Link>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Sections</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">Academic</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Sections
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption />
              <div className="mb-2">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add_class_section"
                >
                  <i className="ti ti-square-rounded-plus-filled me-2" />
                  Add Section
                </Link>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">
                Class Section — {currentBranch?.name || "Current Campus"}
              </h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="input-icon-start mb-3 me-2 position-relative" style={{ minWidth: 200 }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search section name"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
                <div className="mb-3 me-2" style={{ minWidth: 160 }}>
                  <CommonSelect
                    className="select"
                    options={activeList}
                    value={statusFilter}
                    onChange={(opt) => setStatusFilter(opt)}
                  />
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              <Table
                columns={columns}
                dataSource={filteredSections}
                Selection={true}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        {/* Add Section */}
        <div className="modal fade" id="add_class_section">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Add Section</h4>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Section</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newSectionName}
                        onChange={(e) => setNewSectionName(e.target.value)}
                      />
                      <small className="text-muted">
                        Creates this section for every campus for the current academic year.
                      </small>
                    </div>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="status-title">
                        <h5>Status</h5>
                        <p>Change the Status by toggle</p>
                      </div>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={newSectionActive}
                          onChange={(e) => setNewSectionActive(e.target.checked)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <Link to="#" className="btn btn-light me-2" data-bs-dismiss="modal">
                  Cancel
                </Link>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={creating}
                  onClick={handleAddSection}
                >
                  {creating ? "Adding..." : "Add Section"}
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* /Add Section */}

        {/* Edit Section */}
        <div className="modal fade" id="edit_class_section">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Edit Section</h4>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Section</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingSection?.name || ""}
                        onChange={(e) =>
                          setEditingSection((prev) =>
                            prev ? { ...prev, name: e.target.value } : prev
                          )
                        }
                      />
                      <small className="text-muted">
                        Only renames this campus's own section — other campuses keep theirs.
                      </small>
                    </div>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="status-title">
                        <h5>Status</h5>
                        <p>Change the Status by toggle</p>
                      </div>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={editingSection?.status === "active"}
                          onChange={(e) =>
                            setEditingSection((prev) =>
                              prev
                                ? { ...prev, status: e.target.checked ? "active" : "inactive" }
                                : prev
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <Link to="#" className="btn btn-light me-2" data-bs-dismiss="modal">
                  Cancel
                </Link>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={savingEdit}
                  onClick={handleSaveEdit}
                >
                  {savingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* /Edit Section */}

        {/* Delete Section */}
        <div className="modal fade" id="delete-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center">
                <span className="avatar avatar-xl bg-transparent-danger text-danger mb-3">
                  <i className="ti ti-trash-x fs-36" />
                </span>
                <h4 className="mb-1">Delete Section</h4>
                <p className="mb-3">
                  Are you sure you want to delete section "{deletingSection?.name}"? This cannot
                  be undone.
                </p>
                <div className="d-flex justify-content-center">
                  <Link to="#" className="btn btn-light me-3" data-bs-dismiss="modal">
                    Cancel
                  </Link>
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={deleting}
                    onClick={handleConfirmDelete}
                  >
                    {deleting ? "Deleting..." : "Yes, Delete"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Delete Section */}
      </div>
    </div>
  );
};

export default ClassSection;
